import { CSSProperties, useRef, useCallback, useState, memo } from 'react'
import { useTimeOfDay } from '../../hooks/useTimeOfDay'

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error' | 'notification'

interface OrbProps {
  size?: 'full' | 'compact'
  state?: OrbState
  volume?: number
  onTap?: () => void
  onLongPress?: () => void
}

export const DynamicOrb = memo(function DynamicOrb({ size = 'full', state = 'idle', volume = 0, onTap, onLongPress }: OrbProps) {
  useTimeOfDay()
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const didLongPress = useRef(false)
  const [isPressed, setIsPressed] = useState(false)

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    setIsPressed(true)
    didLongPress.current = false
    if (onLongPress) {
      longPressTimer.current = setTimeout(() => {
        didLongPress.current = true
        onLongPress()
      }, 300)
    }
  }, [onLongPress])

  const handlePointerUp = useCallback(() => {
    setIsPressed(false)
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    if (!didLongPress.current && onTap) {
      onTap()
    }
  }, [onTap])

  const handlePointerCancel = useCallback(() => {
    setIsPressed(false)
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const isCompact = size === 'compact'
  const isInteractive = !!(onTap || onLongPress)
  const vol = Math.max(0, Math.min(1, volume))
  const isError = state === 'error'
  const isNotification = state === 'notification'

  // Error state uses red tones, otherwise use BurnOut lava palette
  const orbColors = isError ? {
    primary: '#ef4444',
    secondary: '#991b1b',
    tertiary: '#dc2626',
    glow: 'rgba(239,68,68,0.3)'
  } : {
    primary: '#FF6B00',     // Hot orange core
    secondary: '#F21490',   // Pink / magenta bleed
    tertiary: '#FF4500',    // Deep red-orange
    glow: 'rgba(255,107,0,0.3)'
  }

  // Animation speed: faster when speaking/listening, glacial when thinking, slow pulse for error
  const speedMul = state === 'speaking' ? 0.4
    : state === 'listening' ? 0.5
    : state === 'thinking' ? 0.3
    : state === 'error' ? 1.5
    : 1

  const pressScale = isPressed && isInteractive ? 0.95 : 1

  if (isCompact) {
    return (
      <div
        style={{
          position: 'relative', width: 48, height: 48, flexShrink: 0,
          touchAction: 'manipulation'
        }}
        className="orb-container"
        data-state={state}
        data-size="compact"
        onPointerDown={isInteractive ? handlePointerDown : undefined}
        onPointerUp={isInteractive ? handlePointerUp : undefined}
        onPointerCancel={isInteractive ? handlePointerCancel : undefined}
        onPointerLeave={isInteractive ? handlePointerCancel : undefined}
      >
        <div style={{
          width: '100%', height: '100%', borderRadius: '50%',
          background: `radial-gradient(circle at 40% 40%, ${orbColors.primary}, ${orbColors.secondary})`,
          filter: 'blur(1px)',
          opacity: state === 'thinking' ? 0.6 : isError ? 0.7 : 0.9,
          animation: isNotification
            ? 'orb-breathe 4s ease-in-out infinite, orb-notification-pulse 2s ease-in-out infinite'
            : 'orb-breathe 4s ease-in-out infinite',
          cursor: isInteractive ? 'pointer' : 'default',
          WebkitTapHighlightColor: 'transparent',
          transform: `scale(${pressScale})`,
          transition: 'transform 0.1s ease'
        }} />
      </div>
    )
  }

  // ---- Full orb: lava lamp / ChatGPT-style ----
  const vScale = (1 + vol * 0.15) * pressScale

  const containerStyle: CSSProperties = {
    position: 'relative',
    width: 'min(60vw, 280px)',
    height: 'min(60vw, 280px)',
    cursor: isInteractive ? 'pointer' : 'default',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    touchAction: 'manipulation'
  }

  // Each blob: saturated gradient, heavy blur, screen blend
  const blobs: Array<{
    gradient: string
    size: string
    inset: string
    blur: number
    morphDur: number
    moveDur: number
    opacity: number
    delay: number
  }> = [
    {
      // Base fill — large, saturated, slow morph
      gradient: `radial-gradient(circle at 50% 50%, ${orbColors.primary} 0%, ${orbColors.secondary} 60%, transparent 85%)`,
      size: '100%',
      inset: '0',
      blur: 20,
      morphDur: 12,
      moveDur: 0,
      opacity: 0.85,
      delay: 0
    },
    {
      // Lava blob A — drifts upper-left area
      gradient: `radial-gradient(ellipse at 40% 40%, ${orbColors.secondary} 0%, ${orbColors.primary}99 40%, transparent 70%)`,
      size: '65%',
      inset: '5%',
      blur: 25,
      morphDur: 7,
      moveDur: 10,
      opacity: 0.8,
      delay: 0
    },
    {
      // Lava blob B — drifts lower-right
      gradient: `radial-gradient(ellipse at 60% 60%, ${orbColors.tertiary} 0%, ${orbColors.secondary}88 45%, transparent 75%)`,
      size: '55%',
      inset: '25%',
      blur: 22,
      morphDur: 9,
      moveDur: 14,
      opacity: 0.7,
      delay: -4
    },
    {
      // Hot spot — small bright core, fast morph
      gradient: `radial-gradient(circle at 50% 50%, ${orbColors.primary} 0%, ${orbColors.tertiary}99 35%, transparent 60%)`,
      size: '40%',
      inset: '30%',
      blur: 18,
      morphDur: 5,
      moveDur: 8,
      opacity: 0.9,
      delay: -2
    },
    {
      // White-hot highlight — gives volumetric depth
      gradient: 'radial-gradient(circle at 45% 42%, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.15) 30%, transparent 55%)',
      size: '45%',
      inset: '20%',
      blur: 12,
      morphDur: 8,
      moveDur: 11,
      opacity: isError ? 0.2 : 0.5,
      delay: -1
    }
  ]

  return (
    <div
      style={containerStyle}
      className="orb-container"
      data-state={state}
      data-size="full"
      onPointerDown={isInteractive ? handlePointerDown : undefined}
      onPointerUp={isInteractive ? handlePointerUp : undefined}
      onPointerCancel={isInteractive ? handlePointerCancel : undefined}
      onPointerLeave={isInteractive ? handlePointerCancel : undefined}
    >
      {/* Ambient glow — very soft, extends far beyond the orb */}
      <div style={{
        position: 'absolute',
        inset: '-30%',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${orbColors.glow} 0%, transparent 65%)`,
        opacity: 0.2 + vol * 0.5,
        transition: 'opacity 120ms ease-out',
        pointerEvents: 'none',
        animation: isNotification ? 'orb-notification-pulse 2s ease-in-out infinite' : undefined
      }} />

      {/* Glass container — clips the lava blobs, animated blob shape */}
      <div style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '60% 40% 50% 50% / 50% 60% 40% 50%',
        overflow: 'hidden',
        animation: `orb-blob-morph 12s ease-in-out infinite`,
        boxShadow: `inset 0 0 30px 5px rgba(255,255,255,0.04), 0 0 ${40 + vol * 30}px ${15 + vol * 20}px ${orbColors.glow}`,
        transition: 'box-shadow 150ms ease-out'
      }}>
        {/* Dark interior to make blobs pop */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: isError ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.35)',
          borderRadius: '50%'
        }} />

        {/* Film grain overlay — subtle analog texture */}
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.06\'/%3E%3C/svg%3E")',
          pointerEvents: 'none',
          zIndex: 2,
          mixBlendMode: 'overlay',
          opacity: 0.4
        }} />

        {/* The lava blobs */}
        {blobs.map((blob, i) => {
          const animations: string[] = [
            `orb-blob-morph ${blob.morphDur * speedMul}s ease-in-out ${blob.delay}s infinite`
          ]
          if (blob.moveDur > 0) {
            animations.push(`orb-blob-move ${blob.moveDur * speedMul}s ease-in-out ${blob.delay}s infinite`)
          }

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: blob.inset,
                left: blob.inset,
                width: blob.size,
                height: blob.size,
                borderRadius: '50%',
                background: blob.gradient,
                filter: `blur(${blob.blur}px)`,
                mixBlendMode: 'screen',
                opacity: (state === 'thinking' ? blob.opacity * 0.4 : isError ? blob.opacity * 0.6 : blob.opacity) + vol * 0.1,
                animation: animations.join(', '),
                transform: `scale(${vScale})`,
                transition: 'transform 80ms ease-out, opacity 200ms ease-out',
                pointerEvents: 'none'
              }}
            />
          )
        })}
      </div>
    </div>
  )
})
